import React, { useState, useEffect } from 'react';
import {
  Typography, Box, Button, IconButton, Tooltip, useTheme, useMediaQuery,
  Avatar, CircularProgress
} from '@mui/material';
import { FiTrash2, FiArrowLeft, FiUser, FiPlus } from 'react-icons/fi';
import { DataGrid, GridToolbarContainer, GridToolbarExport } from '@mui/x-data-grid';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../../api';
import Swal from 'sweetalert2';
import AddStudentModal from '../AddStudentModal';

const StudentList = ({ title = "Students", filterStatus, icon = <FiUser /> }) => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRows, setSelectedRows] = useState([]);
  const [error, setError] = useState(null);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const statusQuery = queryParams.get('status'); // "Active", "Inactive", or null

  // Fetch students on mount
  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      const response = await api.get('/users');
      const defaultAvatar = "https://ui-avatars.com/api/?name=Student&background=ddd";

      const normalized = response.data.map(student => ({
        ...student,
        phone: student.phone,
        class: student.usertype || 'N/A',
        lastActive: student.last_active || 'N/A',
        status: student.status === 1 ? 'active' : 'inactive',
        photo: student.profile_image
          ? `http://127.0.0.1:8000/storage/profile_images/${student.profile_image}`
          : defaultAvatar,
      }));

      const filtered = filterStatus
        ? normalized.filter(s => s.status.toLowerCase() === filterStatus.toLowerCase())
        : normalized;

      setStudents(filtered);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch students.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddStudent = (newStudent) => {
    setStudents(prev => [...prev, newStudent]);
    setIsAddModalOpen(false); // close modal after adding
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "Once deleted, you cannot recover this student!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/users/${id}`);
        setStudents(prev => prev.filter(student => student.id !== id));
        Swal.fire('Deleted!', 'The student has been deleted.', 'success');
      } catch (err) {
        console.error(err);
        Swal.fire('Error!', 'Failed to delete student.', 'error');
      }
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm("Delete selected students?")) return;
    try {
      await Promise.all(selectedRows.map(id => api.delete(`/users/${id}`)));
      setStudents(prev => prev.filter(student => !selectedRows.includes(student.id)));
      setSelectedRows([]);
    } catch (err) {
      console.error(err);
      alert("Failed to delete selected students.");
    }
  };

  const columns = [
    {
      field: 'photo',
      headerName: 'Photo',
      width: 80,
      renderCell: (params) => (
        <Avatar
          src={params.row.photo}
          alt={params.row.name}
          sx={{ width: 40, height: 40, mr: 1 }}
        />
      )
    },
    { field: 'name', headerName: 'Name', flex: 1 },
    { field: 'email', headerName: 'Email', flex: 1.2 },
    {
      field: 'phone', headerName: 'Contact', flex: 1, renderCell: (params) => {
        return params.row.phone && params.row.phone.trim() !== '' ? params.row.phone : 'NA';
      }
    },
    { field: 'usertype', headerName: 'User Type', flex: 0.8 },
    { field: 'lastActive', headerName: 'Last Active', flex: 1 },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title="Delete">
          <IconButton color="error" onClick={() => handleDelete(params.row.id)}>
            <FiTrash2 />
          </IconButton>
        </Tooltip>
      )
    }
  ];

  const CustomToolbar = () => (
    <GridToolbarContainer>
      <GridToolbarExport csvOptions={{ fileName: title.replace(/\s+/g, '_').toLowerCase() }} />
      {selectedRows.length > 0 && (
        <Button
          variant="outlined"
          color="error"
          size="small"
          onClick={handleBulkDelete}
          sx={{ ml: 2 }}
        >
          Delete Selected ({selectedRows.length})
        </Button>
      )}
    </GridToolbarContainer>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', p: 2 }}>
      {/* Header Section */}
      <Box
        mb={3}
        display="flex"
        flexDirection={isSmall ? 'column' : 'row'}
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        textAlign={isSmall ? 'center' : 'left'}
      >
        {/* Back Button */}
        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            borderRadius: '12px',
            p: '8px 12px',
            background: 'linear-gradient(135deg, #00bcd4, #007bff)',
            color: '#fff',
            '&:hover': { background: 'linear-gradient(135deg, #007bff, #00bcd4)', transform: 'scale(1.05)' }
          }}
        >
          <FiArrowLeft />
          {!isSmall && (
            <Typography variant="button" sx={{ ml: 1, fontWeight: 600 }}>
              Back
            </Typography>
          )}
        </IconButton>

        {/* Title & Icon */}
        <Box display="flex" alignItems="center" justifyContent="center" gap={1} flex={1}>
          <Box
            sx={{
              fontSize: '2rem',
              background: '#e3f2fd',
              borderRadius: '50%',
              p: 1,
              animation: 'pulseIcon 1.6s infinite'
            }}
          >
            {icon}
          </Box>
          <Typography
            variant={isSmall ? 'h6' : 'h5'}
            sx={{
              fontWeight: 'bold',
              background: 'linear-gradient(90deg, #007BFF, #00d4ff)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textTransform: 'uppercase',
              letterSpacing: '1.2px',
              px: 1
            }}
          >
            {title}
          </Typography>
        </Box>

        {/* Add Student Button */}
        {/* Add Student Button */}
        {!filterStatus && (  // only show if filterStatus is not set
          <Box display="flex" justifyContent={isSmall ? 'center' : 'flex-end'}>
            <Button
              variant="contained"
              color="success"
              startIcon={<FiPlus />}
              sx={{ minWidth: 140 }}
              onClick={() => setIsAddModalOpen(true)}
            >
              Add Student
            </Button>

            <AddStudentModal
              isOpen={isAddModalOpen}
              onClose={() => setIsAddModalOpen(false)}
              onAddStudent={handleAddStudent}
            />
          </Box>
        )}

      </Box>

      {/* DataGrid Section */}
      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error" align="center" mt={4}>{error}</Typography>
      ) : (
        <Box sx={{ width: '100%', height: 500, backgroundColor: '#fff', borderRadius: 2, flexGrow: 1, overflow: 'hidden' }}>
          <DataGrid
            rows={students.filter(s =>
              !statusQuery || String(s.status).toLowerCase() === statusQuery.toLowerCase()
            )}
            autoHeight={false}
            rowHeight={56}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            checkboxSelection
            disableSelectionOnClick
            onSelectionModelChange={(ids) => setSelectedRows(ids)}
            components={{ Toolbar: CustomToolbar }}
            pagination
            loading={loading}
            initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
            sx={{
              fontFamily: 'Segoe UI',
              border: '1px solid #ddd',
              boxShadow: 2,
              '& .MuiDataGrid-columnHeaders': { background: 'linear-gradient(to right, #e0f3ff, #d5faff)', color: '#004c99', fontWeight: 'bold', fontSize: '1rem' },
              '& .MuiDataGrid-row:hover': { backgroundColor: '#fef7e0' },
              '& .MuiDataGrid-cell': { fontSize: '0.95rem' }
            }}
          />
        </Box>
      )}
    </Box>
  );
}

export default StudentList;
